export const SESSION_SCOPES = Object.freeze({
  ADMIN: "admin",
  DRIVER: "driver",
});

export const SESSION_INVALIDATED_EVENT = "app:session-invalidated";

const STORAGE_CONFIG = {
  [SESSION_SCOPES.ADMIN]: {
    sessionKey: "admin_session_v2",
    tokenKey: "auth_token_v2",
    legacySessionKeys: ["admin_session"],
    legacyTokenKeys: ["auth_token"],
  },
  [SESSION_SCOPES.DRIVER]: {
    sessionKey: "driver_session_v2",
    tokenKey: "driver_token_v2",
    legacySessionKeys: ["driver_session_v1"],
    legacyTokenKeys: ["driver_token_v1"],
  },
};

const PERSISTED_FIELDS = {
  [SESSION_SCOPES.ADMIN]: ["id", "email", "name", "role"],
  [SESSION_SCOPES.DRIVER]: [
    "id",
    "email",
    "driverId",
    "name",
    "lastName",
    "dni",
    "phone",
    "taxiPlate",
    "plate",
    "companytaxi",
    "photo",
    "averageRate",
    "totalRatings",
    "ratingsCount",
    "role",
  ],
};

const getBrowserStorage = (type) => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return type === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

const readStorageValue = (storage, key) => {
  if (!storage || !key) return null;

  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorageValue = (storage, key, value) => {
  if (!storage || !key) return;

  try {
    storage.setItem(key, value);
  } catch {
    return;
  }
};

const removeStorageValue = (storage, key) => {
  if (!storage || !key) return;

  try {
    storage.removeItem(key);
  } catch {
    return;
  }
};

const parseJson = (value) => {
  if (!value) return null;

  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const decodeBase64Url = (value) => {
  if (!value || typeof atob !== "function") return "";

  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padding = normalized.length % 4;
  const padded = padding ? normalized.padEnd(normalized.length + (4 - padding), "=") : normalized;

  try {
    return decodeURIComponent(
      atob(padded)
        .split("")
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join("")
    );
  } catch {
    try {
      return atob(padded);
    } catch {
      return "";
    }
  }
};

const parseJwtPayload = (token) => {
  if (!token || typeof token !== "string") return null;

  const parts = token.split(".");
  if (parts.length < 2) return null;

  return parseJson(decodeBase64Url(parts[1]));
};

const normalizeLegacySession = (scope, rawSession) => {
  if (!rawSession) return null;

  if (scope === SESSION_SCOPES.DRIVER && rawSession.driver) {
    return rawSession.driver;
  }

  return rawSession;
};

const pickDefinedFields = (scope, session) => {
  const allowedFields = PERSISTED_FIELDS[scope] || [];

  return allowedFields.reduce((acc, field) => {
    if (session?.[field] !== undefined && session?.[field] !== null && session?.[field] !== "") {
      acc[field] = session[field];
    }
    return acc;
  }, {});
};

const getScopeConfig = (scope) => STORAGE_CONFIG[scope] || null;

const readLegacyToken = (scope) => {
  const config = getScopeConfig(scope);
  if (!config) return "";

  const sessionStorage = getBrowserStorage("session");

  for (const key of config.legacyTokenKeys) {
    const token = readStorageValue(sessionStorage, key);
    if (token) return token;
  }

  return "";
};

const readLegacySession = (scope) => {
  const config = getScopeConfig(scope);
  if (!config) return null;

  const sessionStorage = getBrowserStorage("session");
  const localStorage = getBrowserStorage("local");

  for (const key of config.legacySessionKeys) {
    const sessionValue = readStorageValue(sessionStorage, key) || readStorageValue(localStorage, key);
    const parsed = parseJson(sessionValue);

    if (parsed) {
      return normalizeLegacySession(scope, parsed);
    }
  }

  return null;
};

const clearLegacyEntries = (scope) => {
  const config = getScopeConfig(scope);
  if (!config) return;

  const sessionStorage = getBrowserStorage("session");
  const localStorage = getBrowserStorage("local");

  [...config.legacySessionKeys, ...config.legacyTokenKeys].forEach((key) => {
    removeStorageValue(sessionStorage, key);
    removeStorageValue(localStorage, key);
  });
};

export const getTokenExpiryTime = (token) => {
  const payload = parseJwtPayload(token);
  return typeof payload?.exp === "number" ? payload.exp * 1000 : null;
};

export const isTokenExpired = (token, toleranceMs = 30000) => {
  const expiryTime = getTokenExpiryTime(token);
  return expiryTime ? expiryTime <= Date.now() + toleranceMs : false;
};

const dispatchInvalidationEvent = (scope, reason) => {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent(SESSION_INVALIDATED_EVENT, {
      detail: { scope, reason },
    })
  );
};

export const clearPersistedSession = (scope, options = {}) => {
  const config = getScopeConfig(scope);
  if (!config) return;

  const sessionStorage = getBrowserStorage("session");
  const localStorage = getBrowserStorage("local");

  removeStorageValue(sessionStorage, config.sessionKey);
  removeStorageValue(sessionStorage, config.tokenKey);
  removeStorageValue(localStorage, config.sessionKey);
  removeStorageValue(localStorage, config.tokenKey);
  clearLegacyEntries(scope);

  if (options.broadcast) {
    dispatchInvalidationEvent(scope, options.reason || "invalidated");
  }
};

export const persistSession = (scope, session, token) => {
  const config = getScopeConfig(scope);
  const sessionStorage = getBrowserStorage("session");

  if (!config || !sessionStorage || !session || !token || isTokenExpired(token)) {
    clearPersistedSession(scope);
    return null;
  }

  const sanitizedSession = pickDefinedFields(scope, session);

  writeStorageValue(sessionStorage, config.sessionKey, JSON.stringify(sanitizedSession));
  writeStorageValue(sessionStorage, config.tokenKey, token);
  clearLegacyEntries(scope);

  return {
    session: sanitizedSession,
    token,
    expiresAt: getTokenExpiryTime(token),
  };
};

export const restorePersistedSession = (scope) => {
  const config = getScopeConfig(scope);
  if (!config) return null;

  const sessionStorage = getBrowserStorage("session");
  const storedSession = parseJson(readStorageValue(sessionStorage, config.sessionKey));
  const storedToken = readStorageValue(sessionStorage, config.tokenKey) || "";

  if (storedSession && storedToken) {
    if (isTokenExpired(storedToken)) {
      clearPersistedSession(scope);
      return null;
    }

    return {
      session: pickDefinedFields(scope, storedSession),
      token: storedToken,
      expiresAt: getTokenExpiryTime(storedToken),
    };
  }

  const legacySession = readLegacySession(scope);
  const legacyToken = readLegacyToken(scope);

  if (!legacySession || !legacyToken) {
    clearLegacyEntries(scope);
    return null;
  }

  return persistSession(scope, legacySession, legacyToken);
};

export const invalidateSession = (scope, reason = "expired") => {
  clearPersistedSession(scope, { broadcast: true, reason });
};
